import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy,ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { FormGroup, Validators, FormArray } from '@angular/forms';

import { ModuleConfiguration } from '../submission.module.config';
import { SubmittingEntityModel } from '../models/SubmittingEntityModel';
import * as fromStore from '../store/reducers';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { PhysicalAddressModel } from '../../common/models/PhysicalAddressModel';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { AbstractControl } from '@angular/forms/src/model';
import { Subject } from 'rxjs/Subject';
import { ContactModel } from '../models/ContactModel';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';
import { ConfigureReportingAction } from '../store/actions/ConfigureReportingAction';
import { CONFIGURE_REPORTING_SUCCESS } from '../store/actions/ConfigureReportingSuccessAction';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { MatDialog } from '@angular/material';
import { DeleteSubmissionConfirmationDialogComponent } from './delete-submission-confirmation-dialog.component';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { SubmittingEntityIdentifierModel } from '../models/SubmittingEntityIdentifierModel';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';
import { autoSaveNotReadyValidator } from "../../common/validators/autoSaveNotReadyValidator";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'brd-submitting-entity-workspace',
  templateUrl: './submitting-entity-workspace.component.html',
  styleUrls: ['./submitting-entity-workspace.component.scss']
})
export class SubmittingEntityWorkspaceComponent implements OnInit, OnDestroy {
  submittingEntity: SubmittingEntityModel;
  key: string;
  loading$: Observable<boolean>;
  reportingNotConfigured$: Observable<boolean>;
  errors$: Observable<string[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  profileForm: FormGroup;
  allowedDataSetsForm: FormGroup;
  contactsForm: FormGroup;
  jobSubmissionReportsForm: FormGroup;
  formArrayInitMethods = new Map<string, () => void>();
  editPermission: string = 'SubmissionModule.SubmittingEntity:Edit';
  enableDisableAutoSftpXmlJobReports = 'SubmissionModule.SftpXmlJobReports:EnableDisable';
  isPortal: boolean = false;
  nameIsReadOnly: boolean = true;
  nameIsReadyToSave: boolean = true;

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private currentUserService: CurrentUserService,
    private appConfigurationService: AppConfigurationService) {
      this.nameIsReadOnly = appConfigurationService.configuration.isDelegatingSubmittingEntityManagementToOtherSystem;
  }


  ngOnDestroy() {
    this.store.dispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, SubmittingEntityModel));
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.loading$ = this.store.select(fromStore.getSubmittingEntityListLoading);
    this.reportingNotConfigured$ = this.store.select(fromStore.getSubmittingEntityIsReportingNotConfigured);
    this.errors$ = this.store.select(fromStore.getSubmittingEntityListErrors);
    this.route.params.take(1).subscribe(p => {
      this.key = p['key'];

      this.store.dispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, SubmittingEntityModel, { key: this.key }));
    });
    this.route.queryParams.subscribe(qp => {
      var isPortalValue = qp['isPortal'];
      if (isPortalValue) {
        this.isPortal = isPortalValue;
      }
    });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, 'name'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, SubmittingEntityIdentifierModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, 'allowedDataSets'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, 'reviseAutoGenerateSftpXmlJobReports')
    ];
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t)
        && a.type !== workspaceActions.updateModelSuccessTypeByUpdateType(workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, 'name')))))
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<SubmittingEntityModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(
        workspaceActions.updateModelType(ModuleConfiguration.moduleName, SubmittingEntityModel, 'name')))))
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<SubmittingEntityModel, any>) => {
        var control = this.profileForm.controls['name'] as AutoSaveFormGroup;
        if (control.value !== a.payload.model) {
          this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, 'name', { key: this.key, model: control.value, control: control }));
        } else {
          a.payload.control.saveSuccess(a.payload.model);
          this.nameIsReadyToSave = true;
        }
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .subscribe((a: workspaceActions.UpdateModelErrorAction<SubmittingEntityModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });
    var removeAddressType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel);
    this.actions$.ofType(removeAddressType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var addressesFormArray = (this.profileForm.controls['addresses'] as FormArray);
        var index = addressesFormArray.controls.indexOf(payload.control);
        addressesFormArray.removeAt(index);
      });
    var removeContactType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel);
    this.actions$.ofType(removeContactType)
      .takeUntil(this.destroyed$)
        .map((a: any) => a.payload)
        .subscribe((payload) => {
          payload.control.saveSuccess(payload.model);
          var contactsArray = (this.contactsForm.controls['contacts'] as FormArray);
          var index = contactsArray.controls.indexOf(payload.control);
          contactsArray.removeAt(index);
        });
    this.store.select(fromStore.getSubmittingEntitySelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: SubmittingEntityModel) => {
        if (v) {
          this.submittingEntity = v;
          if (!this.profileForm) {
            this.initForms();
          }
          this.changeDetectorRef.markForCheck();
        }
      });

    this.actions$.ofType(CONFIGURE_REPORTING_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.alerts.addSuccess("Successfully Configured Reporting!");
      });
  }

  initForms(): void {
    this.profileForm = this.formBuilder.group({
      name: this.formBuilder.control('',
        Validators.compose([
          Validators.required,
          (control: AbstractControl) => autoSaveNotReadyValidator(this.nameIsReadyToSave)(control)
        ]),
        null,
        this.destroyed$,
        (control) => {
          this.nameIsReadyToSave = false;
          this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, 'name', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
      identifiers: this.formBuilder.array(this.submittingEntity.identifiers.map(i => this.initIdentifiers())),
      addresses: this.formBuilder.array(this.submittingEntity.addresses.map(a => this.initAddress()))
    });
    this.allowedDataSetsForm = this.formBuilder.group({
      allowedDataSets: this.formBuilder.array(this.submittingEntity.allowedDataSets.map( a => this.initAllowedDataset()))},
        null,
        this.destroyed$,
        (control) => {
          this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, 'allowedDataSets', { key: this.key, model: control.value.allowedDataSets, control: control }));
        },
        this.changeDetectorRef
    );
    this.contactsForm = this.formBuilder.group({
      contacts: this.formBuilder.array(this.submittingEntity.contacts.map(c => this.initContact()))
    });
    this.jobSubmissionReportsForm = this.formBuilder.group({
      autoGenerateSftpXmlJobReports: this.formBuilder.control('',
        null,
        null,
        this.destroyed$,
        (control) => {
          this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, Boolean, { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef)
    });    

    this.formArrayInitMethods.set('identifiers', () => this.initIdentifiers());
    this.formArrayInitMethods.set('addresses', () => this.initAddress());
    this.formArrayInitMethods.set('contacts', () => this.initContact());

    this.profileForm.patchValue(this.submittingEntity);
    this.profileForm.markAsPristine();
    this.contactsForm.patchValue(this.submittingEntity);
    this.contactsForm.markAsPristine();
    this.allowedDataSetsForm.patchValue(this.submittingEntity);
    this.allowedDataSetsForm.markAsPristine();
    this.jobSubmissionReportsForm.patchValue(this.submittingEntity);
    this.jobSubmissionReportsForm.markAsPristine();
    
    if(this.appConfigurationService.configuration.hasSubmittingEntityIdentifier && this.profileForm.controls['identifiers']['length'] == 0){
      this.addFormGroup(this.profileForm, 'identifiers');
    }    
  }

  initIdentifiers() {
    var formGroup = this.formBuilder.group({
      key: [''],
      identifierType: ['', Validators.required],
      identifier: ['', 
      Validators.compose([
        Validators.required,
        Validators.maxLength(50)
      ])]
    },
      null,
      this.destroyed$,
      (control) => {
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, SubmittingEntityIdentifierModel, { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    return formGroup;
  }

  initAddress() {
    var formGroup = this.formBuilder.group({
      key: [''],
      type: ['', Validators.required],
      firstStreetAddress: ['', Validators.required],
      secondStreetAddress: [''],
      cityName: ['', Validators.required],
      stateProvince: ['', Validators.required],
      postalCode: ['', Validators.required],
    },
      null,
      this.destroyed$,
      (control) => {
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel, { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    return formGroup;
  }

  initAllowedDataset(): FormGroup {
    var formGroup = this.formBuilder.group({
      dataSetFullName: [''],
      dataSetDisplayName: [''],
      canSubmit: [''],
      canUseDataEntryScreens: [''],
      dataEntryScreensEnabled: ['']
    });
    return formGroup;
  }

  initContact() {
    return this.formBuilder.group({
      key: [''],
      name: ['', Validators.required],
      number: ['', Validators.compose([Validators.required, Validators.pattern(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/)])],
      email: ['', (c: AbstractControl) => {
        if (!c.value) {
          return null;
        }
        return Validators.email(c);
      }
      ],
    },
      null,
      this.destroyed$,
      (control) => {
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel, { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);
  }

  removeAddress(form, address: AutoSaveFormGroup, index) {
    address.initializeRemove();
    this.store.dispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel, { key: this.key, model: address.value, control: address }));
  }

  addFormGroup(form, name) {
    form.controls[name].push(this.formArrayInitMethods.get(name)());
  }

  removeContact(form, contact: AutoSaveFormGroup, index) {
    contact.initializeRemove();
    this.store.dispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel, { key: this.key, model: contact.value, control: contact }));
  }

  configureReporting() {
    this.store.dispatch(new ConfigureReportingAction({ submittingEntityKey: this.key }));
  }

  back() {
    this.router.navigateByUrl('/administration');
  }
  delete() {
    this.dialog.open(DeleteSubmissionConfirmationDialogComponent,
      {
        panelClass: 'customized-dialog',
        width: '648px',
        data:
        {
            name: this.submittingEntity.name,
            key: this.key
        }
      });
  }

  canToggleAutomatedReports() {
    return this.currentUserService.hasPermission(this.enableDisableAutoSftpXmlJobReports);
  }
}



// WEBPACK FOOTER //
// ./src/app/submission/components/submitting-entity-workspace.component.ts